import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector
} from '@reduxjs/toolkit'
import {
  searchData,
  DataSearchResponseItem,
  DataSearchRequest
} from '../../services/endpoints/data'
import { RequestErrorResponse } from '../../services/lib'
import { formatError } from '../lib/errors'
import { renderDateTime } from '../../utils/dateFormatter'

const sliceName = 'data'

export interface DataStateVersionItem {
  type: string
  selected: boolean
  size: number
  startDate: string
  endDate: string
  fileName: string
  vehicleName: string
  vehicleDisplayName: string
  deviceId: number
  archived: string | null
  checkedOut: string | null
}

export interface DataStateVersions {
  [id: number]: DataStateVersionItem
}

export interface DataStateFileTypes {
  [id: string]: boolean
}
export type DataStatePaging = {
  max: number //max records per page
  offset: number //current number of records offset
  total: number //Total records for query
}
export interface DataState {
  fileTypes: DataStateFileTypes
  versions: DataStateVersions
  data: DataSearchResponseItem[] | null
  paging: DataStatePaging | null
  loading: boolean
  error: RequestErrorResponse | null
  lastRequest: DataSearchRequest | null
  groups: string[]
  unmatchedFileVersionIds: number[]
}

export type PartialSearchRequest =
  | DataSearchRequest
  | { sort: string; order: 'desc' | 'asc' }
  | { offset: number; max?: number }
  | undefined

const _getSearchRequest = (
  req: PartialSearchRequest,
  lastReq: DataSearchRequest | null
): DataSearchRequest | null => {
  if (req === undefined) {
    return lastReq
  } else if (req['vehicleId'] !== undefined) {
    //hate this, but don't feel like writting a proper function to "confirm"
    return req as DataSearchRequest
  } else if (req['offset'] !== undefined) {
    return lastReq === null
      ? lastReq
      : {
          ...lastReq,
          offset: req['offset'],
          max: req['max'] ?? lastReq.max
        }
  } else if (req['sort'] !== undefined && req['order'] !== undefined) {
    return lastReq === null
      ? lastReq
      : {
          ...lastReq,
          sort: req['sort'],
          order: req['order']
        }
  } else {
    //console.error('Error calculating next search request, request arg was invalid', req)
    return lastReq === null
      ? lastReq
      : {
          ...lastReq,
          sort: 'uploadId',
          order: 'desc'
        }
  }
}

//Handles partial searches using previous request
export const searchDataThunk = createAsyncThunk<
  {
    paging: DataStatePaging
    data: DataSearchResponseItem[]
    usedRequest: DataSearchRequest
  } | null,
  PartialSearchRequest,
  { state: { data: DataState } }
>(`${sliceName}/search`, async (req, { rejectWithValue, getState }) => {
  const finalRequest = _getSearchRequest(req, getState().data.lastRequest)

  if (!finalRequest) {
    return null
  }

  try {
    return { ...(await searchData(finalRequest)), usedRequest: finalRequest }
  } catch (err: any) {
    return rejectWithValue(err)
  }
})

export const selectDataSelectionMode = createSelector(
  (state: { data: DataState }) => state.data.versions,
  (versions) => {
    let hasLocal = false
    let hasNonLocal = false

    Object.values(versions).forEach((v) => {
      if (!v.selected) return
      const isLocal = !v.archived || !!v.checkedOut
      if (isLocal) {
        hasLocal = true
      } else {
        hasNonLocal = true
      }
    })

    return { hasLocalSelected: hasLocal, hasNonLocalSelected: hasNonLocal }
  }
)

const generateOtherData = (
  data: DataSearchResponseItem[] | null
): [DataStateFileTypes, DataStateVersions] => {
  const fileTypes: DataStateFileTypes = {}
  const versions: DataStateVersions = {}

  if (data) {
    data.forEach((u) => {
      u.versions.forEach((v) => {
        if (!(v.extension in fileTypes)) {
          fileTypes[v.extension] = false
        }

        versions[v.id] = {
          type: v.extension,
          size: v.size,
          selected: false,
          startDate: renderDateTime({ value: u.startDate }) || 'Unknown',
          endDate: renderDateTime({ value: u.endDate }) || 'Unknown',
          fileName: u.displayName,
          vehicleName: u.vehicle.name,
          vehicleDisplayName: u.vehicle.displayName,
          deviceId: u.device.id,
          archived: v.archived,
          checkedOut: v.checkedOut
        }
      })
    })
  }

  return [fileTypes, versions]
}

export const initialDataState: DataState = {
  fileTypes: {},
  versions: {},
  data: [],
  loading: false,
  error: null,
  lastRequest: null,
  paging: null,
  groups: [],
  unmatchedFileVersionIds: []
}

export const dataSlice = createSlice({
  name: sliceName,
  initialState: initialDataState,
  reducers: {
    toggleVersionsByType: (state, action: PayloadAction<string>) => {
      const type = action.payload

      if (!(type in state.fileTypes)) {
        console.error(
          `Trying to toggle file type state for ${type} which doesn't exist`
        )
        return
      }

      const newState = !state.fileTypes[type]
      state.fileTypes[type] = newState

      if (state.versions === null) {
        console.error(
          'Trying to toggle file type state for all versions but versions do not exist'
        )
        return
      }

      Object.keys(state.versions).forEach((vid) => {
        const v = state.versions[vid]
        if (v.type === type) {
          // Only allow selecting versions that are local (not archived or checked out)
          const isLocal = !v.archived || !!v.checkedOut
          if (isLocal) {
            v.selected = newState
          }
        }
      })
    },
    toggleVersion: (state, action: PayloadAction<number>) => {
      const vid = action.payload

      if (!state.versions || !(vid in state.versions)) {
        console.error(
          `Trying to change version state for ${vid} which doesn't exist`
        )
        return
      }

      const newState = !state.versions[vid].selected

      if (!newState && state.fileTypes[state.versions[vid].type]) {
        state.fileTypes[state.versions[vid].type] = false
      }

      state.versions[vid].selected = newState
    },
    clearAllSelections: (state) => {
      Object.keys(state.fileTypes).forEach((extType) => {
        state.fileTypes[extType] = false
      })

      Object.keys(state.versions).forEach((vid) => {
        state.versions[vid].selected = false
      })
    }
  },
  extraReducers(builder) {
    builder
      .addCase(searchDataThunk.fulfilled, (state, action) => {
        if (action.payload) {
          const [types, versions] = generateOtherData(action.payload.data)
          state.groups = []
          const newData = action.payload.data.map((i) => {
            if (state.groups.indexOf(i.groupId || '') === -1) {
              state.groups.push(i.groupId || '')
            }
            i.groupId = `${state.groups.indexOf(i.groupId || '')}`
            return i
          })
          state.lastRequest = action.payload.usedRequest
          state.fileTypes = types
          state.versions = versions
          state.data = newData
          state.paging = action.payload.paging

          // Compute unmatched file version IDs and auto-select matched versions
          const requestedFileVersions =
            action.payload.usedRequest.fileVersions || []
          if (requestedFileVersions.length > 0) {
            const matchedSet = new Set<number>()
            const requestedSet = new Set(requestedFileVersions)

            Object.keys(versions).forEach((vid) => {
              const versionId = Number(vid)
              if (requestedSet.has(versionId)) {
                matchedSet.add(versionId)
                versions[versionId].selected = true
              }
            })

            state.unmatchedFileVersionIds = requestedFileVersions.filter(
              (id) => !matchedSet.has(id)
            )
          } else {
            state.unmatchedFileVersionIds = []
          }
        } else {
          state.data = []
          state.unmatchedFileVersionIds = []
        }
        state.error = null
        state.loading = false
      })
      .addCase(searchDataThunk.pending, (state, _action) => {
        state.fileTypes = {}
        state.versions = {}
        state.data = null
        state.error = null
        state.loading = true
        state.groups = []
        state.unmatchedFileVersionIds = []
      })
      .addCase(searchDataThunk.rejected, (state, action) => {
        state.fileTypes = {}
        state.versions = {}
        state.data = null
        state.paging = null
        state.error = action.payload ? formatError(action.payload) : null
        state.loading = false
        state.groups = []
        state.unmatchedFileVersionIds = []
      })
  }
})
